import {
  BuildingCandidate,
  GeocodeAttempt,
  HmlrMatch,
} from "@inrange/building-manager-api-client";
import { useEffect, useState } from "react";
import {
  CircleMarker,
  MapContainer,
  Polygon,
  TileLayer,
  useMap,
  useMapEvent,
} from "react-leaflet";
import { useBuildingsByBounds } from "@inrange/api-clients";
import { polygonBbox } from "./leadGeo";
import {
  distanceToPolygonMetres,
  matchConfidence,
} from "./buildingMatchConfidence";

// Google Maps satellite tiles for the right-panel maps. The hybrid
// layer (`lyrs=y`) stacks roads + labels on top of imagery so the
// operator can read street names while still seeing rooftops.
const TILE_URL = "https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}";
const TILE_ATTRIBUTION = "© Google";

// Initial map zoom on mount and the zoom we restore to whenever the
// operator picks a new location source. Selecting a source re-anchors
// the green pin; we always pan it to the centre + reset zoom so the
// operator gets a consistent view regardless of how they'd zoomed
// the previous building/polygon fit.
export const INITIAL_MAP_ZOOM = 17;

// One-shot map viewport requests dispatched by the right-panel
// section headers (e.g. "View all locations", "View all buildings"
// or "fly to clicked location"). The component fires the requested
// viewport change once, then nulls the request out so the map doesn't
// keep snapping back when the operator manually pans.
export type MapViewportRequest =
  | { kind: "fly-to"; lat: number; lon: number; zoom?: number }
  | { kind: "fit-polygon"; polygon: BuildingCandidate["polygon"] }
  | { kind: "all-locations" }
  | { kind: "all-buildings" };

const ApplyMapViewportRequest: React.FC<{
  request: MapViewportRequest | null;
  onApplied: () => void;
  locationPoints: [number, number][];
  buildingPolygons: BuildingCandidate["polygon"][];
}> = ({ request, onApplied, locationPoints, buildingPolygons }) => {
  const map = useMap();
  useEffect(() => {
    if (!request) return;
    if (request.kind === "fly-to") {
      // Default to the current zoom (pan-only) - the building-fit
      // request from polygon clicks is what should set zoom level,
      // not transient pin / tenant-row hovers. Callers can still
      // pass an explicit zoom when they want to override.
      // No fly animation - snap straight to the target. The animated pan
      // reads as distracting when the map jumps into place on a click.
      map.setView([request.lat, request.lon], request.zoom ?? map.getZoom(), {
        animate: false,
      });
    } else if (request.kind === "fit-polygon") {
      // Fit to the polygon's vertices (not the centroid) so the whole
      // building is in frame. A 22,000 m^2 distribution depot's
      // bounding box is ~150m x 150m - flying to the centroid at zoom
      // 18 only shows part of it. fitBounds with maxZoom 19 picks the
      // tightest zoom that keeps the whole footprint visible.
      const flat: [number, number][] = [];
      for (const ring of polygonToLatLngs(request.polygon)) flat.push(...ring);
      if (flat.length === 0) {
        onApplied();
        return;
      }
      map.fitBounds(flat, { padding: [30, 30], maxZoom: 19, animate: false });
    } else if (request.kind === "all-locations") {
      if (locationPoints.length === 0) {
        onApplied();
        return;
      }
      const bounds = locationPoints.map(
        ([la, lo]) => [la, lo] as [number, number]
      );
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 18, animate: false });
    } else if (request.kind === "all-buildings") {
      const flat: [number, number][] = [];
      for (const poly of buildingPolygons) {
        for (const ring of polygonToLatLngs(poly)) flat.push(...ring);
      }
      if (flat.length === 0) {
        onApplied();
        return;
      }
      map.fitBounds(flat, { padding: [40, 40], maxZoom: 18, animate: false });
    }
    onApplied();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request]);
  return null;
};

const polygonToLatLngs = (
  poly: BuildingCandidate["polygon"] | null | undefined
): [number, number][][] => {
  // GeoJSON: Polygon is [ring][[lon,lat],...], MultiPolygon adds a
  // top-level array. Leaflet wants [[lat,lon],...]. CSV-imported
  // leads can have null `polygon` (no building resolved yet) so we
  // defensively skip empty/null input - returning [] lets callers
  // render zero polygons rather than crashing on `ring.map(...)`.
  if (!poly || !Array.isArray(poly) || poly.length === 0) return [];
  const isMulti = Array.isArray(poly?.[0]?.[0]?.[0]);
  const polys = isMulti ? (poly as number[][][][]) : [poly as number[][][]];
  return polys
    .flat()
    .filter(
      (ring): ring is number[][] => Array.isArray(ring) && ring.length > 0
    )
    .map((ring) => ring.map(([lon, lat]) => [lat, lon] as [number, number]));
};

// Convert a raw OSM building (from useBuildingsByBounds - the datasets-api
// buildings-in-bounds source that /site/new uses) into a BuildingCandidate
// so a "Show all available buildings" click drops straight into the same
// candidate pipeline (table + map + save) as a resolver-offered polygon.
const osmToCandidate = (
  b: any,
  selectedLocation: [number, number] | null,
  declaredFloorAreaSqm: number | null
): BuildingCandidate | null => {
  const polygon = b?.geometry?.coordinates as
    | BuildingCandidate["polygon"]
    | undefined;
  if (!polygon) return null;
  const area =
    typeof b.surface_area_sqm === "number"
      ? b.surface_area_sqm
      : typeof b.surfaceAreaSqM === "number"
        ? b.surfaceAreaSqM
        : null;
  const bbox = polygonBbox(polygon);
  const centroidLat = bbox
    ? (bbox[1] + bbox[3]) / 2
    : (selectedLocation?.[0] ?? 0);
  const centroidLon = bbox
    ? (bbox[0] + bbox[2]) / 2
    : (selectedLocation?.[1] ?? 0);
  const distanceMetres = selectedLocation
    ? distanceToPolygonMetres(polygon, selectedLocation[0], selectedLocation[1])
    : 0;
  const confidence = selectedLocation
    ? matchConfidence(distanceMetres, area, declaredFloorAreaSqm)
    : null;
  return {
    bkey: `osm:${b.key ?? `${centroidLon},${centroidLat}`}`,
    polygon,
    centroidLat,
    centroidLon,
    surfaceAreaSqm: area,
    distanceMetres,
    confidence,
    score: null,
    proximityWeight: null,
    areaWeight: null,
    areaMatch: null,
    pinOverlapsPolygon: distanceMetres === 0,
    belowAreaMatchFloor: false,
  };
};

// Map layer for the "Show all available buildings" toggle. Fetches every
// OSM building in the current viewport (datasets-api, the same source and
// zoom gate /site/new uses - it only fetches when zoomed in under ~7km)
// and renders them as clickable purple outlines. Clicking one calls
// `onPick` with a synthesised candidate. Buildings already in the resolved
// candidate list are skipped so we don't double-draw them.
const AllBuildingsLayer: React.FC<{
  existingBkeys: Set<string>;
  selectedLocation: [number, number] | null;
  declaredFloorAreaSqm: number | null;
  onPick: (cand: BuildingCandidate) => void;
}> = ({ existingBkeys, selectedLocation, declaredFloorAreaSqm, onPick }) => {
  const map = useMap();
  const [bounds, setBounds] = useState(() => map.getBounds());
  useMapEvent("moveend", (e) => setBounds(e.target.getBounds()));
  const query = useBuildingsByBounds(bounds);
  const buildings = query.data ? Object.values(query.data) : [];
  return (
    <>
      {buildings.map((b) => {
        const cand = osmToCandidate(b, selectedLocation, declaredFloorAreaSqm);
        if (cand === null || existingBkeys.has(cand.bkey)) return null;
        return (
          <Polygon
            key={cand.bkey}
            positions={polygonToLatLngs(cand.polygon)}
            pathOptions={{
              color: "#6f42c1",
              weight: 2.5,
              opacity: 0.95,
              fillColor: "#6f42c1",
              fillOpacity: 0.3,
            }}
            eventHandlers={{ click: () => onPick(cand) }}
          />
        );
      })}
    </>
  );
};

// The right panel's satellite map: candidate polygons, the geocoder pins, the
// transient HMLR-deed / tenant overlays, and the optional "every OSM building"
// layer. Purely presentational - every piece of state it renders and every
// action it raises is a required prop, so the panel stays the only place that
// decides what is selected.
export const LeadPanelMap: React.FC<{
  mapCentre: [number, number] | null;
  viewportRequest: MapViewportRequest | null;
  onViewportApplied: () => void;
  geocodeAttempts: GeocodeAttempt[];
  // Every candidate the table offers, including an off-list OSM building the
  // operator clicked. These are the polygons drawn.
  candidates: BuildingCandidate[];
  // The resolver-offered candidates only - what a "fit all buildings" request
  // frames, so an exploratory OSM pick doesn't widen the view.
  resolvedCandidates: BuildingCandidate[];
  selectedBkey: string | null;
  hoveredBkey: string | null;
  bkeyToColor: Record<string, string> | null;
  onSelectBuilding: (bkey: string) => void;
  showAllBuildings: boolean;
  selectedLocation: [number, number] | null;
  declaredFloorAreaSqm: number | null;
  onPickOsmBuilding: (cand: BuildingCandidate) => void;
  hoveredHmlrTitleNumber: string | null;
  hmlrMatches: HmlrMatch[];
  hoveredTenantLatLon: [number, number] | null;
  hoveredLocationLatLon: [number, number] | null;
  onSelectLocation: (latLon: [number, number]) => void;
}> = ({
  mapCentre,
  viewportRequest,
  onViewportApplied,
  geocodeAttempts,
  candidates,
  resolvedCandidates,
  selectedBkey,
  hoveredBkey,
  bkeyToColor,
  onSelectBuilding,
  showAllBuildings,
  selectedLocation,
  declaredFloorAreaSqm,
  onPickOsmBuilding,
  hoveredHmlrTitleNumber,
  hmlrMatches,
  hoveredTenantLatLon,
  hoveredLocationLatLon,
  onSelectLocation,
}) => (
  <div
    style={{
      height: 400,
      marginBottom: 12,
      borderRadius: 4,
      overflow: "hidden",
      background: "#eee",
    }}
  >
    {mapCentre ? (
      <MapContainer
        center={mapCentre}
        zoom={INITIAL_MAP_ZOOM}
        style={{ height: "100%", width: "100%" }}
      >
        <TileLayer attribution={TILE_ATTRIBUTION} url={TILE_URL} />
        <ApplyMapViewportRequest
          request={viewportRequest}
          onApplied={onViewportApplied}
          locationPoints={geocodeAttempts
            .filter((a) => a.lat != null && a.lon != null)
            .map((a) => [a.lat as number, a.lon as number])}
          buildingPolygons={resolvedCandidates.map((b) => b.polygon)}
        />
        {candidates.map((b) => {
          const isSelected = b.bkey === selectedBkey;
          const isHovered = b.bkey === hoveredBkey;
          const isMultiCandidate = bkeyToColor != null;
          // Orange (BUILDING_PALETTE[0]) is the auto-selected /
          // highest-confidence building - single-candidate falls back to
          // it, multi-candidate gets it as index 0 - so it matches the
          // orange footprint the overview map draws for the lead.
          const baseColour = bkeyToColor?.[b.bkey] ?? "#fd7e14";
          return (
            <Polygon
              // Include baseColour in the key so a buildingCandidates
              // length change (1 -> 2+) forces a remount; react-
              // leaflet's pathOptions diff doesn't always re-tint
              // the layer when it's been mounted under a different
              // colour, and the old colour can stick.
              key={`${b.bkey}-${baseColour}`}
              positions={polygonToLatLngs(b.polygon)}
              pathOptions={{
                // Each building keeps its identity colour in all states -
                // selected is the saturated/opaque form, hovered slightly
                // less, default dimmed - so the table's Color column maps
                // cleanly to the polygon on the map.
                color: baseColour,
                weight: isSelected
                  ? 4
                  : isHovered
                    ? 3
                    : isMultiCandidate
                      ? 2
                      : 1.5,
                opacity: isSelected ? 1 : isHovered ? 0.85 : 0.5,
                dashArray: isSelected || isMultiCandidate ? undefined : "4 6",
                fillColor: baseColour,
                // Deliberately light. The operator is judging the ROOF
                // through this fill - its condition, its obstructions, how
                // much of it is plant - so the polygon has to identify the
                // building without obscuring what they came to look at.
                // The stroke carries the identity instead: selected is a
                // 4px solid outline, which reads from across the map at a
                // fill this faint. Ordering still runs selected > hovered >
                // candidate > other, so a row's colour maps to its polygon.
                fillOpacity: isSelected
                  ? 0.28
                  : isHovered
                    ? 0.18
                    : isMultiCandidate
                      ? 0.1
                      : 0.05,
              }}
              eventHandlers={{
                // The buildingBkey-driven useEffect handles the
                // map fit; we just have to update state.
                click: () => onSelectBuilding(b.bkey),
              }}
            />
          );
        })}
        {/* "Show all available buildings" - every OSM building in the
                  viewport, clickable to pick one the resolver didn't offer.
                  Rendered before the HMLR/tenant overlays so those stay on
                  top; the candidate polygons above sit on top of these. */}
        {showAllBuildings && (
          <AllBuildingsLayer
            existingBkeys={new Set(candidates.map((c) => c.bkey))}
            selectedLocation={selectedLocation}
            declaredFloorAreaSqm={declaredFloorAreaSqm}
            onPick={onPickOsmBuilding}
          />
        )}
        {/* Transient HMLR deed polygon - rendered when the operator
                  hovers an HMLR row in the Landlord / Owner-occupier
                  section. Cleared on mouseleave. Purple to distinguish
                  from the orange/green building polygons; non-clickable
                  (the source-of-truth click target is the row itself).
                  rendered after the building polygons so it sits on top. */}
        {hoveredHmlrTitleNumber &&
          (() => {
            const hovered = hmlrMatches.find(
              (m) => m.titleNumber === hoveredHmlrTitleNumber
            );
            if (!hovered?.polygon) return null;
            return (
              <Polygon
                key={`hmlr-hover-${hoveredHmlrTitleNumber}`}
                positions={polygonToLatLngs(hovered.polygon)}
                pathOptions={{
                  color: "#6f42c1",
                  weight: 2.5,
                  fillOpacity: 0.15,
                  interactive: false,
                }}
              />
            );
          })()}
        {/* Hovered Places/OSM tenant - pink CircleMarker. Pink
                  so it's visually distinct from the orange/green
                  location pins (geocoder attempts) and the purple
                  HMLR deed overlay. Non-clickable. */}
        {hoveredTenantLatLon && (
          <CircleMarker
            key={`tenant-hover-${hoveredTenantLatLon[0]}-${hoveredTenantLatLon[1]}`}
            center={hoveredTenantLatLon}
            radius={8}
            pathOptions={{
              color: "#d63384",
              weight: 2,
              fillColor: "#d63384",
              fillOpacity: 0.5,
              interactive: false,
            }}
          />
        )}
        {/* Render every geocoder attempt that returned a coordinate.
                  Selected = green, unselected = orange (mirrors the
                  building polygon colour scheme). Clicking a pin
                  re-anchors selectedLocation. */}
        {geocodeAttempts
          .filter((a) => a.lat != null && a.lon != null)
          .map((a, i) => {
            const isSelected =
              selectedLocation != null &&
              a.lat === selectedLocation[0] &&
              a.lon === selectedLocation[1];
            const isHovered =
              hoveredLocationLatLon != null &&
              a.lat === hoveredLocationLatLon[0] &&
              a.lon === hoveredLocationLatLon[1];
            return (
              <CircleMarker
                key={`${a.source}-${i}`}
                center={[a.lat as number, a.lon as number]}
                radius={isSelected || isHovered ? 9 : 5}
                pathOptions={{
                  color: isSelected
                    ? "#28a745"
                    : isHovered
                      ? "#198754"
                      : "#fd7e14",
                  weight: isSelected || isHovered ? 3 : 2,
                  fillColor: isSelected
                    ? "#28a745"
                    : isHovered
                      ? "#198754"
                      : "#fd7e14",
                  fillOpacity: isSelected ? 0.7 : isHovered ? 0.5 : 0.4,
                }}
                eventHandlers={{
                  click: () =>
                    onSelectLocation([a.lat as number, a.lon as number]),
                }}
              />
            );
          })}
      </MapContainer>
    ) : (
      <div
        style={{
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#888",
          fontSize: 12,
        }}
      >
        No location resolved
      </div>
    )}
  </div>
);
